import type { IInputDatasetResponse } from '@escloud/types';

import { Badge } from '@/components/ui/badge';
import { formatDicomDate, formatDicomName } from '@/lib/dicom';
import { cn } from '@/lib/utils';

interface SeriesSummaryCardProps {
    dataset: IInputDatasetResponse | null;
    isLoading?: boolean;
}

/**
 * Compact summary of the picked dataset, rendered beneath the
 * tabbed MPR viewer in the wizard's right pane.
 *
 * Renders the patient / study eyebrow, modality / accession key-value
 * rows, the per-series clinical fields extracted on commit
 * (accession number, study and series UID), and a Technical-details
 * disclosure for internal UIDs and SHA-256.
 *
 * No suitability verdict here — Phase 3 ingests the raw fields and
 * leaves clinical judgement to the surgeon.
 */
export function SeriesSummaryCard({ dataset, isLoading }: SeriesSummaryCardProps) {
    if (!dataset) {
        return (
            <div className="rounded-md bg-card p-3 text-xs text-muted-foreground">
                {isLoading ? 'Loading dataset details…' : 'Select a dataset to see its details.'}
            </div>
        );
    }

    return (
        <div className="rounded-md bg-card p-3 text-xs">
            <header className="mb-2 flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">{dataset.seriesDescription ?? 'Series'}</p>
                    <p className="truncate text-[11px] text-muted-foreground">
                        {[
                            formatDicomName(dataset.patientName) ?? dataset.patientId,
                            formatDicomDate(dataset.studyDate),
                            dataset.studyDescription,
                        ]
                            .filter(Boolean)
                            .join(' · ') || 'No patient context'}
                    </p>
                </div>
                {dataset.modality && (
                    <Badge variant="secondary" className="shrink-0">
                        {dataset.modality}
                    </Badge>
                )}
            </header>

            <dl className="grid grid-cols-[5rem_1fr] gap-x-3 gap-y-1">
                <SummaryRow label="Accession" value={dataset.accessionNumber} mono />
                {dataset.studyInstanceUid && (
                    <SummaryRow label="Study UID" value={dataset.studyInstanceUid} mono />
                )}
                {dataset.seriesInstanceUid && (
                    <SummaryRow label="Series UID" value={dataset.seriesInstanceUid} mono />
                )}
            </dl>

            <details className="mt-3 text-[11px] text-muted-foreground">
                <summary className="cursor-pointer select-none">Technical details</summary>
                <dl className="mt-1 grid grid-cols-[5rem_1fr] gap-x-3 gap-y-0.5 break-all font-mono">
                    <dt className="text-muted-foreground/70">Dataset id</dt>
                    <dd>{dataset.id}</dd>
                    <dt className="text-muted-foreground/70">SHA-256</dt>
                    <dd>{dataset.contentSha256}</dd>
                </dl>
            </details>
        </div>
    );
}

function SummaryRow({
    label,
    value,
    mono,
}: {
    label: string;
    value: string | null | undefined;
    mono?: boolean;
}) {
    const display = value ?? '—';
    return (
        <>
            <dt className="text-muted-foreground">{label}</dt>
            <dd className={cn('truncate', mono && 'font-mono text-[10px]')} title={display}>
                {display}
            </dd>
        </>
    );
}
